/**
 * engineNormalJson.ts - NormalJson template engine
 * Pure ES Module version
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { MergeResult } from '../../../../Assembler/dist/model/mergeResult.js';
import type { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import { IEngine } from '../../../../Assembler/dist/base/engine/iEngine.js';
import type { IEngineTimings } from '../../../../Assembler/dist/base/engine/iEngineTimings.js';
import { EngineTimingData } from '../../../../Assembler/dist/model/engineTimingData.js';
import type { ILoaderNormalJson } from '../loader/iLoaderNormalJson.js';

// Import from split modules
import {
    helperMethods
} from './engineNormalJson_Helper.js';

import { templateTraversalMethods } from './engineNormalJson_Merge_TemplateTraversal.js';
import { mergeArrayMethods } from './engineNormalJson_Array.js';
import { mergeTemplates, prepareProtectedMergeContext, initializeDynamicMergeState, collectDynamicHandlerResults, createProtectedMergeContext, createProtectedMergeConfig } from './engineNormalJson_Merge.js';
import { mergeArrayBlockMethods } from './engineNormalJson_Merge_ArrayBlock.js';
import { mergeConditionalMethods } from './engineNormalJson_Merge_Conditional.js';
import { mergePlaceholderMethods } from './engineNormalJson_Merge_Placeholder.js';
import { mergeSlotholderMethods } from './engineNormalJson_Merge_Slotholder.js';

export type { ILoaderNormalJson };

/**
 * NormalJson engine implementation that processes HtmlJson/DataJson structures directly
 * Similar to EnginePreProcessJson but WITHOUT metadata - resolves components on-the-fly using loader
 * Returns {componentHtml: Map, operations} tuple for consistency with other engines
 */
class EngineNormalJson extends IEngine implements IEngineTimings {
    // Loader provided at construction time - ensures type safety
    _loader: ILoaderNormalJson | null = null;

    // Timing instrumentation (instance-scoped) - all times in nanoseconds
    _getTemplateNanos: number = 0;
    _jsonMergeNanos: number = 0;
    _slotProcessNanos: number = 0;
    _placeholderProcessNanos: number = 0;
    _dynamicDataNanos: number = 0;
    _dynamicViewNanos: number = 0;
    _dynamicActionNanos: number = 0;
    _dynamicClientDataNanos: number = 0;
    _getTemplateCount: number = 0;
    _jsonMergeCount: number = 0;
    _slotProcessCount: number = 0;
    _placeholderProcessCount: number = 0;
    _dynamicDataCount: number = 0;
    _dynamicViewCount: number = 0;
    _dynamicActionCount: number = 0;
    _dynamicClientDataCount: number = 0;

    /**
     * Convert milliseconds to nanoseconds
     */
    static msToNanos(ms: number): number {
        const nanos = ms * 1_000_000;
        if (!Number.isFinite(nanos)) {
            return 0;
        }
        return Math.round(nanos);
    }

    resetTimings(): void {
        this._getTemplateNanos = 0;
        this._jsonMergeNanos = 0;
        this._slotProcessNanos = 0;
        this._placeholderProcessNanos = 0;
        this._dynamicDataNanos = 0;
        this._dynamicViewNanos = 0;
        this._dynamicActionNanos = 0;
        this._dynamicClientDataNanos = 0;
        this._getTemplateCount = 0;
        this._jsonMergeCount = 0;
        this._slotProcessCount = 0;
        this._placeholderProcessCount = 0;
        this._dynamicDataCount = 0;
        this._dynamicViewCount = 0;
        this._dynamicActionCount = 0;
        this._dynamicClientDataCount = 0;
    }

    getTimingReport(): string {
        // Convert nanos to ms for display
        return `[NormalJson Timing] GetTemplate: ${(this._getTemplateNanos / 1_000_000.0).toFixed(2)}ms (${this._getTemplateCount} calls), ` +
            `JsonMerge: ${(this._jsonMergeNanos / 1_000_000.0).toFixed(2)}ms (${this._jsonMergeCount} calls), ` +
            `SlotProcess: ${(this._slotProcessNanos / 1_000_000.0).toFixed(2)}ms (${this._slotProcessCount} calls), ` +
            `PlaceholderProcess: ${(this._placeholderProcessNanos / 1_000_000.0).toFixed(2)}ms (${this._placeholderProcessCount} calls), ` +
            `DynamicData: ${(this._dynamicDataNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicDataCount} calls), ` +
            `DynamicView: ${(this._dynamicViewNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicViewCount} calls), ` +
            `DynamicAction: ${(this._dynamicActionNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicActionCount} calls), ` +
            `DynamicClientData: ${(this._dynamicClientDataNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicClientDataCount} calls)`;
    }

    getTimingData(): EngineTimingData {
        // Values are stored in nanoseconds
        const timingData = new EngineTimingData();
        timingData.getTemplateNanos = this._getTemplateNanos;
        timingData.getTemplateCount = this._getTemplateCount;
        timingData.jsonMergeNanos = this._jsonMergeNanos;
        timingData.jsonMergeCount = this._jsonMergeCount;
        timingData.slotProcessNanos = this._slotProcessNanos;
        timingData.slotProcessCount = this._slotProcessCount;
        timingData.placeholderProcessNanos = this._placeholderProcessNanos;
        timingData.placeholderProcessCount = this._placeholderProcessCount;
        timingData.dynamicDataNanos = this._dynamicDataNanos;
        timingData.dynamicDataCount = this._dynamicDataCount;
        timingData.dynamicViewNanos = this._dynamicViewNanos;
        timingData.dynamicViewCount = this._dynamicViewCount;
        timingData.dynamicActionNanos = this._dynamicActionNanos;
        timingData.dynamicActionCount = this._dynamicActionCount;
        timingData.dynamicClientDataNanos = this._dynamicClientDataNanos;
        timingData.dynamicClientDataCount = this._dynamicClientDataCount;
        return timingData;
    }

    accumulateDynamicDataTiming(nanos: number, count: number): void {
        this._dynamicDataNanos += nanos;
        this._dynamicDataCount += count;
    }

    accumulateDynamicViewTiming(nanos: number, count: number): void {
        this._dynamicViewNanos += nanos;
        this._dynamicViewCount += count;
    }

    accumulateDynamicActionTiming(nanos: number, count: number): void {
        this._dynamicActionNanos += nanos;
        this._dynamicActionCount += count;
    }

    accumulateDynamicClientDataTiming(nanos: number, count: number): void {
        this._dynamicClientDataNanos += nanos;
        this._dynamicClientDataCount += count;
    }

    ticksToNanos(nanos: number): number {
        return nanos;
    }

    addSlotProcessTiming(nanos: number): void {
        this._slotProcessNanos += nanos;
        this._slotProcessCount++;
    }

    addPlaceholderProcessTiming(nanos: number): void {
        this._placeholderProcessNanos += nanos;
        this._placeholderProcessCount++;
    }

    addJsonMergeTiming(nanos: number): void {
        this._jsonMergeNanos += nanos;
        this._jsonMergeCount++;
    }

    addGetTemplateTiming(nanos: number): void {
        this._getTemplateNanos += nanos;
        this._getTemplateCount++;
    }

    /**
     * Creates a new EngineNormalJson instance with the specified loader.
     * The loader is stored and used for all subsequent operations, ensuring type safety.
     */
    constructor(loader: ILoaderNormalJson) {
        super();
        if (!loader) {
            throw new Error('loader is required');
        }
        this._loader = loader;
    }

    mergeToBytes(appSite: string, appView: string | null, contextJson: JsonObject | null): Uint8Array {
        const mergedHtml = this.mergeToString(appSite, appView, contextJson);
        return new TextEncoder().encode(mergedHtml);
    }

    mergeToString(appSite: string, appView: string | null, contextJson: JsonObject | null): string {
        const result = this.mergeTemplates(appSite, appView, false, false, contextJson ?? new JsonObject());
        return result.componentHtml.get(result.appFile).asString() || '';
    }

    // Method implemented via Object.assign from engineNormalJson_Merge.ts
    declare mergeTemplates: (appSite: string, appView: string | null, genOperationJson: boolean, genComponentHtml: boolean, contextJson: JsonObject | null, configJson?: JsonObject | null, actionMode?: boolean, clientMode?: boolean) => MergeResult;
    declare processArrayBlock: (resultBuilder: { value: string }, arrayKey: string, arrayBlockData: JsonObject, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[], jsonData: JsonObject, componentHtml: JsonObject | null, genComponentHtml: boolean, recursionDepth: number, contextJson?: JsonObject | null, flowStack?: string[] | null, configJson?: JsonObject | null, clientMode?: boolean) => void;
    declare processArrayBlockEntry: (params: unknown) => string;
    declare wrapJsonArrayWithControlElements: (jsonArray: import('../../../../Assembler/dist/app/json/jsonValue.js').JsonArray | unknown[]) => import('../../../../Assembler/dist/app/json/jsonValue.js').JsonArray;
    declare mergeArrayItemContent: (blockContent: JsonObject, itemData: JsonObject, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[], jsonData: JsonObject, componentHtml: JsonObject | null, genComponentHtml: boolean, processedKeys: Set<string>, recursionDepth: number, contextJson?: JsonObject | null, flowStack?: string[] | null, configJson?: JsonObject | null, clientMode?: boolean) => string;
    declare mergeArrayItemConditionalBlock: (conditionalBlockData: JsonObject, fallbackConditionalKey: string, itemData: JsonObject, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[], jsonData: JsonObject, componentHtml: JsonObject | null, genComponentHtml: boolean, processedKeys: Set<string>, recursionDepth: number, contextJson: JsonObject | null, flowStack: string[] | null, configJson?: JsonObject | null, clientMode?: boolean) => string;
    declare findPairedConditionalBlocks: (conditionalBlockData: JsonObject) => { positiveContent: JsonObject | null; negativeContent: JsonObject | null };
    declare selectConditionalContentForCondition: (conditionMet: boolean, positiveContent: JsonObject | null, negativeContent: JsonObject | null) => JsonObject | null;
    declare mergeTemplate: (htmlJson: JsonObject, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[], jsonData: JsonObject, componentHtml: JsonObject | null, flowStack: string[] | null, genComponentHtml: boolean, contextJson?: JsonObject | null, configJson?: JsonObject | null, recursionDepth?: number, parentComponentSink?: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[] | null, keyReplaceStage?: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[] | null, keyMissingStage?: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[] | null, parentSlotSink?: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[] | null, clientMode?: boolean) => string;
    declare mergeSlotComponent: (slotHtmlJson: JsonObject, slotholderData: JsonObject, appSite: string, slotName: string, appView: string | null, slotKey: string, operations: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[], jsonData: JsonObject, slotContentSourceTemplate: string, componentHtml: JsonObject | null, flowStack: string[] | null, genComponentHtml: boolean, contextJson: JsonObject | null, configJson?: JsonObject | null, recursionDepth?: number, clientMode?: boolean) => string;
    declare processConditionalBlock: (params: unknown) => string;
    declare ProcessPlaceholder: (params: unknown) => { result: string; continue: boolean };
    declare ProcessSlotholder: (params: unknown) => { result: string };
    declare createProtectedMergeContext: (appSite: string, appView: string | null, appFile: string, engineType: string, contextJson: JsonObject) => JsonObject;
    declare createProtectedMergeConfig: (configJson: JsonObject | null) => JsonObject | null;
    declare prepareProtectedMergeContext: (appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null) => { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null };
    declare initializeDynamicMergeState: (appSite: string, appFile: string, appView: string | null, actionMode: boolean) => DynamicResultsJson;
    declare collectDynamicHandlerResults: (dynamicResultsJson: DynamicResultsJson) => { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject };
}

Object.assign(EngineNormalJson.prototype, {
    ...templateTraversalMethods,
    mergeTemplates,
    prepareProtectedMergeContext,
    initializeDynamicMergeState,
    collectDynamicHandlerResults,
    createProtectedMergeContext,
    createProtectedMergeConfig,
    ...helperMethods,
    ...mergeArrayBlockMethods,
    ...mergeConditionalMethods,
    ...mergePlaceholderMethods,
    ...mergeSlotholderMethods,
    ...mergeArrayMethods
});

// ES6 export
export { EngineNormalJson };
